<template>
  <div class="modals-container">
    <VModal v-if="permissionsError || authError" :showing="showRequestAccess" :hide-close="hideCloseBtn" container-classes="max-w-xl">
      <div v-if="!showRequestForm" class="flex flex-col my-4">
        <template v-if="!userState.user">
          <h2 class="text-2xl mb-4 text-center">This 3D Presentation is Confidential</h2>
          <p class="mb-6">Have a VRIFY account? Log in to continue. If you need access, request it from the presentation owner.</p>
          <VButton :component="'a'" :href="loginUrl" class="block w-full text-center" variant="copper">Log In</VButton>
          <div class="my-4 text-gray-500 text-center">Or</div>
          <VButton class="w-full text-center" variant="copper-outline" @click="showRequestForm = true">Request Access</VButton>
        </template>
        <template v-else>
          <h2 class="text-2xl mb-4 text-center">You don't have access to this 3D Presentation.</h2>
          <p class="mb-6">Your account doesn't have permission to view this 3D Presentation. Request access from the presentation owner.</p>
          <VButton class="w-full text-center" variant="copper" @click="showRequestForm = true">Request Access</VButton>
        </template>
      </div>
      <div v-else class="flex flex-col space-y-6 my-4 w-128">
        <div class="text-center">
          <h2 class="text-2xl mb-2">REQUEST ACCESS</h2>
          <p class="text-sm">Fill out the form to request access to this page</p>
        </div>
        <form class="text-md space-y-4" @submit.prevent="onSubmit">
          <FormField
            v-model="name"
            :v-bind="nameAttrs"
            name="name"
            type="text"
            label="Name"
            placeholder="Enter your name"
            autocomplete="off"
            required
            :error="errors.name"
          />
          <FormField
            v-model="email_address"
            :v-bind="email_addressAttrs"
            name="email_address"
            type="email"
            label="Email Address"
            placeholder="Enter your email address"
            autocomplete="off"
            required
            :error="errors.email_address"
          />
          <FormField
            v-model="reason"
            :v-bind="reasonAttrs"
            name="reason"
            type="text"
            label="Reason"
            placeholder="Enter the reason for your access request"
            autocomplete="off"
            required
            :error="errors.reason"
          />
          <div class="flex gap-x-4 justify-between pt-6">
            <VButton class="w-full text-center" variant="secondary" @click="showRequestForm = false">Back</VButton>
            <VButton class="w-full text-center" variant="copper" @click="onSubmit">Request Access</VButton>
          </div>
        </form>
      </div>
    </VModal>
    <VModal v-if="linkExpiredError" v-model:showing="linkExpiredError" :hide-close="true" container-classes="max-w-xl">
      <div v-if="!linkRenewalSuccess" class="flex flex-col my-4">
        <h2 class="text-2xl mb-4 text-center">THIS 3D PRESENTATION LINK HAS EXPIRED.</h2>
        <p class="mb-6">If you still have access to this presentation, you can request a new link below.</p>
        <VButton class="w-full text-center" variant="copper" @click="renewSecureLink">Request New Link</VButton>
      </div>
      <div v-else class="flex flex-col my-4 text-center">
        <h2 class="text-2xl mb-4">Request Sent</h2>
        <p>If you still have access, you'll receive an email with a new access link shortly.</p>
      </div>
    </VModal>
  </div>
</template>

<script lang="ts" setup>
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import z from 'zod'

const props = defineProps({
  // TODO: this needs a better name
  project: {
    type: String,
    required: false,
    default: 'project',
  },

  content: {
    type: String,
    required: false,
    default: 'content',
  },

  showLogin: {
    type: Boolean,
    required: false,
    default: false,
  },

  showSignUp: {
    type: Boolean,
    required: false,
    default: false,
  },

  hasAuthError: {
    type: Boolean,
    required: false,
    default: false,
  },

  hasPermissionsError: {
    type: Boolean,
    required: false,
    default: false,
  },

  shouldRefresh: {
    type: Boolean,
    default: true,
  },

  authErrorCopy: {
    type: String,
    required: false,
    default: '',
  },
  hideClose: {
    type: Boolean,
    default: false,
  },

  hasLinkExpiredError: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const route = useRoute()

const showRequestForm = ref(false)
const permissionsError = ref(false)
const authError = ref(false)
const linkExpiredError = ref(false)
const linkRenewalSuccess = ref(false)
const { state: userState } = useUser()
const config = useRuntimeConfig()
const { notification, setNotification } = useGlobalNotification()

const emit = defineEmits(['update:hasAuthError'])

const shouldHideClose = computed(() => {
  return permissionsError.value || !userState.value.user
})

const showRequestAccess = computed(() => props.hasAuthError || props.hasPermissionsError)

const hideCloseBtn = computed(() => {
  return props.hideClose && shouldHideClose.value
})

const authErrorMessage = computed(() => {
  if (props.authErrorCopy && props.authErrorCopy.length > 0) {
    return props.authErrorCopy
  } else {
    return `Please log in to access ${props.project}`
  }
})

const loginUrl = computed(() => {
  const params = new URLSearchParams()

  // Add existing query params
  Object.entries(route.query).forEach(([key, value]) => {
    if (value) {
      const stringValue = Array.isArray(value) ? value[0] : value
      if (stringValue) {
        params.set(key, stringValue)
      }
    }
  })

  // Add redirect param
  params.set('redirectedFrom', route.path)

  const queryString = params.toString()
  return queryString ? `/login?${queryString}` : '/login'
})

watch(
  () => authError.value,
  val => {
    emit('update:hasAuthError', val)
  },
)

watch(
  () => props.showLogin,
  async val => {
    if (val) {
      await navigateTo(
        { path: `${config.public.WEB_ENDPOINT}login`, query: { ...route.query, redirectedFrom: route.path } },
        { external: true },
      )
    }
  },
)

watch(
  () => props.showSignUp,
  async val => {
    if (val) {
      await navigateTo(
        { path: `${config.public.WEB_ENDPOINT}login`, query: { ...route.query, redirectedFrom: route.path } },
        { external: true },
      )
    }
  },
)

watch(
  () => props.hasAuthError,
  val => {
    authError.value = val
  },
  { immediate: true },
)

watch(
  () => props.hasPermissionsError,
  val => {
    permissionsError.value = val
  },
  { immediate: true },
)

watch(
  () => props.hasLinkExpiredError,
  val => {
    linkExpiredError.value = val
    linkRenewalSuccess.value = false
  },
  { immediate: true },
)

const formSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, 'Name is required'),
    email_address: z.string().email('Please enter a valid email address').min(1, 'Email is required'),
    reason: z.string().min(1, 'Reason is required'),
  }),
)

const { handleSubmit, defineField, errors } = useForm({
  validationSchema: formSchema,
  initialValues: {
    name: userState.value.user?.name ?? '',
    email_address: userState.value.user?.email_address ?? '',
    reason: '',
  },
})

const [name, nameAttrs] = defineField('name')
const [email_address, email_addressAttrs] = defineField('email_address')
const [reason, reasonAttrs] = defineField('reason')

const onSubmit = handleSubmit(async values => {
  try {
    await requestDeckAccess(route.params.id as string, {
      name: values.name,
      email: values.email_address,
      reason: values.reason,
    })
  } finally {
    setNotification({
      title: 'Access Request Submitted',
      text: 'Your access request has been submitted. You will be notified via email when it is approved.',
    })
    showRequestForm.value = false
  }
})

async function requestDeckAccess(deckId: string | number, payload: { name: string; email: string; reason: string }): Promise<void> {
  return await $fetch<void>(`/v2/decks/${deckId}/request_access`, {
    ...useFetchSharedOptions(),
    method: 'POST',
    body: payload,
  })
}

async function renewSecureLink(): Promise<void> {
  try {
    const uuid = route.query.auth as string
    if (!uuid) {
      setNotification({
        title: 'Error',
        text: 'Unable to renew link: missing authentication token.',
        type: 'danger',
      })
      return
    }

    await $fetch<void>('/v2/decks/secure_link/renew', {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: { uuid },
    })

    linkRenewalSuccess.value = true
  } catch (error) {
    setNotification({
      title: 'Error',
      text: 'Failed to request new link. Please try again later.',
      type: 'danger',
    })
  }
}
</script>

<style scoped>
.modals-container {
  z-index: 9000;
  position: fixed;
}
</style>
